'use no memo';
'use client';

import { useEffect, useRef, useState } from 'react';
import { Toaster } from 'sonner';
import factions from '@/shared/enums/factions.mjs';
import { EVENT_STATUS } from '@/shared/enums/events.mjs';
import { detectChanges } from '@/shared/utils/game/detectChanges.mjs';
import { getDismissedEvents, addDismissedEvent } from '@/features/notifications/dismissedEvents.mjs';
import { computePulseDelays } from '@/shared/utils/game/pulseDelays.mjs';
import { showEventToast, toastLabel } from '@/features/notifications/EventToast';
function showWebNotification(message, event) {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (!document.hidden) return;
  new Notification(message, {
    icon: factions[event.enemy]?.icon || '/icon.svg',
    badge: '/icon.svg'
  });
}

/**
 * Toast notification layer and Sonner `<Toaster>` host.
 *
 * Right border continuously blinks between faction color and an alert color
 * (danger red for started/lost/catch-up, success green for won), matching the
 * sector-card-action-flash animation timing.
 *
 * Architecture notes:
 * - `'use no memo'` opts out of the React Compiler, which otherwise merges
 *   the two `useEffect` hooks and breaks the catch-up toast.
 * - The `<Toaster>` is co-located here (not in the root layout) so that
 *   `toast()` and `<Toaster>` share the same Sonner module instance.
 *
 * @param {{prevData: object, data: object, isLeader: boolean}} props - Component props
 */
export default function LiveToasts({
  prevData,
  data,
  isLeader
}) {
  const hasRendered = useRef(false);

  // Sonner reads `position` only at mount and ignores prop changes, so we
  // detect viewport once after hydration and let the `key` flip force the
  // Toaster to remount with the correct position. Mobile gets `top-center`
  // to match native iOS/Android push notification placement; desktop keeps
  // `bottom-right`. No resize listener — page-load detection is enough.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 767px)').matches);
  }, []);

  // Catch-up toasts: show active events on page load, plus events that
  // transitioned to a terminal state since the user last dismissed them.
  //
  // Dismissal is tracked per (eventId, status) pair. If the user dismissed
  // an event at status='active' and the event is still active, we skip it
  // entirely. If the event has since transitioned to success/fail, we show
  // the completion toast because the status has meaningfully changed.
  useEffect(() => {
    if (hasRendered.current) return;
    const allEvents = data?.events ?? [];
    if (!allEvents.length) {
      hasRendered.current = true;
      return;
    }
    const timer = setTimeout(() => {
      hasRendered.current = true;
      const dismissed = getDismissedEvents();
      const delays = computePulseDelays(allEvents);
      let shownCount = 0;
      for (const event of allEvents) {
        const dismissedStatus = dismissed[String(event.event_id)]?.status;
        if (dismissedStatus === event.status) continue; // fully suppressed

        if (event.status === EVENT_STATUS.ACTIVE) {
          showEventToast(event, 'catch_up', {
            pulseDelay: delays.get(`${event.enemy}-${event.region}`),
            onDismiss: () => addDismissedEvent(event.event_id, event.status)
          });
          shownCount++;
        } else if (dismissedStatus === EVENT_STATUS.ACTIVE) {
          // User dismissed the active toast; event has since
          // transitioned. Show the terminal outcome so the user
          // doesn't silently miss a status change.
          const kind = event.status === EVENT_STATUS.SUCCESS ? 'event_won' : 'event_lost';
          const alertColor = kind === 'event_won' ? 'var(--color-success)' : 'var(--color-danger)';
          showEventToast(event, kind, {
            alertColor,
            pulseDelay: delays.get(`${event.enemy}-${event.region}`),
            onDismiss: () => addDismissedEvent(event.event_id, event.status)
          });
          shownCount++;
        }
        // else: completed event never dismissed — skip on catch-up
      }
      if (shownCount && window.umami) {
        window.umami.track('toast-catch-up', {
          count: shownCount
        });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Transition toasts: detect event state changes between data updates
  useEffect(() => {
    if (!hasRendered.current) return;
    if (!prevData || !data) return;
    const changes = detectChanges(prevData.events, data.events);
    const delays_0 = computePulseDelays(data?.events);
    for (const change of changes) {
      const {
        event: event_0,
        kind: kind_0
      } = change;
      const alertColor_0 = kind_0 === 'event_won' ? 'var(--color-success)' : 'var(--color-danger)';
      showEventToast(event_0, kind_0, {
        alertColor: alertColor_0,
        pulseDelay: delays_0.get(`${event_0.enemy}-${event_0.region}`),
        onDismiss: () => addDismissedEvent(event_0.event_id, event_0.status)
      });
      if (isLeader) {
        const {
          title
        } = toastLabel(kind_0, event_0);
        showWebNotification(title, event_0);
      }
    }
  }, [data, prevData, isLeader]);
  return <Toaster key={isMobile ? 'mobile' : 'desktop'} theme="dark" position={isMobile ? 'top-center' : 'bottom-right'} closeButton toastOptions={{
    style: {
      borderRadius: '0px',
      background: 'var(--color-surface-1)',
      color: 'var(--color-text)',
      border: '1px solid var(--color-ghost)',
      fontFamily: 'var(--font-body)'
    }
  }} />;
}